import { LucideProps } from 'lucide-react';
import React from 'react';

import { ConditionalRender, Gutter, Skeleton } from '@crossnokaye/ux-common';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/ux-common/components/accordion/accordion';
import {
  Header,
  HeaderActions,
  HeaderIcon,
  HeaderTitle,
} from '@/ux-common/components/header/header';
import { Icon } from '@/ux-common/components/icon/icon';
import { Inline } from '@/ux-common/components/inline/inline';
import { Stack } from '@/ux-common/components/stack/stack';
import { Typography } from '@/ux-common/components/typography/typography';

const isRenderFunction = (
  icon: (() => React.ReactNode) | React.ComponentType<LucideProps>
): icon is () => React.ReactNode => {
  return typeof icon === 'function' && icon.length === 0;
};

interface SectionTemplateProps {
  title: string;
  icon: (() => React.ReactNode) | React.ComponentType<LucideProps>;
  rightButtons?: React.ReactNode;
  children: React.ReactNode;
  isLoading?: boolean;
}

export const SectionTemplate = ({
  title,
  icon,
  rightButtons,
  children,
  isLoading,
}: SectionTemplateProps): React.ReactNode => {
  const IconComponent = icon;
  return (
    <ConditionalRender condition={!isLoading} fallback={<Skeletons children={children} />}>
      <Accordion type={'multiple'} defaultValue={[`sections-${title}`]} id={`sections-${title}`}>
        <AccordionItem className={'border-0'} value={`sections-${title}`}>
          <Gutter orientation="horizontal" size="responsive">
            <AccordionTrigger>
              <Header>
                <HeaderIcon>
                  <Icon className="icon-tier2 icon-lg icon-circle" aria-hidden>
                    {isRenderFunction(icon) ? icon() : <IconComponent aria-hidden />}
                  </Icon>
                </HeaderIcon>
                <HeaderTitle>
                  <Typography as="h2" className="text-category-header">
                    {title}
                  </Typography>
                </HeaderTitle>
                {rightButtons && <HeaderActions>{rightButtons}</HeaderActions>}
              </Header>
            </AccordionTrigger>
          </Gutter>
          <Gutter size="thin" orientation="horizontal" className="sm:px-3 lg:px-5">
            <AccordionContent className={'overflow-hidden py-stack-default'}>
              <Stack>{children}</Stack>
            </AccordionContent>
          </Gutter>
        </AccordionItem>
      </Accordion>
    </ConditionalRender>
  );
};

const Skeletons = ({ children }: { children: React.ReactNode }) => {
  return (
    <Gutter orientation="horizontal" size="responsive" className="mb-stack-default">
      <Header className="p-stack-sm">
        <HeaderIcon>
          <Skeleton className="w-10 h-9" />
        </HeaderIcon>
        <HeaderTitle>
          <Skeleton className="w-40 h-9" />
        </HeaderTitle>
        <HeaderActions>
          <Inline className="h-9">
            <Skeleton className="w-10 h-10" />
          </Inline>
        </HeaderActions>
      </Header>
      {children}
    </Gutter>
  );
};
